// src/components/admin/page-management/VideoUploader.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const VideoUploader = ({ onVideoUpload, pageSlug }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');

  // Log the pageSlug prop whenever it changes
  useEffect(() => {
    console.log('VideoUploader received pageSlug:', pageSlug);
    setDebugInfo(`Current page slug: ${pageSlug || 'none'}`);
  }, [pageSlug]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setError('Video file is too large. Maximum size is 100MB.');
      return;
    }

    // Check file type
    const validTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid video format. Please use MP4, WebM, or OGG.');
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append('video', file);
    
    // Clean the slug
    const cleanSlug = pageSlug ? pageSlug.replace(/[^a-zA-Z0-9-_]/g, '') : 'shared';
    
    // Add to form data too
    formData.append('pageSlug', cleanSlug);
    
    // Debug logs
    console.log(`Uploading video for page: ${cleanSlug}`);
    setDebugInfo(`Uploading for: ${cleanSlug}`);

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Get token from localStorage directly
      const token = localStorage.getItem('token');
      
      // Use both query parameter and form data for the page slug (matching your existing pattern)
      const response = await axios.post(`/api/upload-page-video?pageSlug=${cleanSlug}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      setUploading(false);
      
      // Call the callback with the uploaded video URL
      if (onVideoUpload && response.data && response.data.filePath) {
        console.log('Video uploaded successfully:', response.data.filePath);
        setDebugInfo(`Upload success: ${response.data.filePath}`);
        onVideoUpload(response.data.filePath);
      }
    } catch (err) {
      setUploading(false);
      setError(err.response?.data?.error || 'Error uploading video');
      setDebugInfo(`Upload error: ${err.message}`);
      console.error('Upload error:', err);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center space-x-2">
        <label className="cursor-pointer px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm">
          <span>Choose Video File</span>
          <input
            type="file"
            accept="video/mp4,video/webm,video/ogg"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>
        {uploading && (
          <div className="flex-1 relative h-8 bg-gray-200 rounded overflow-hidden">
            <div 
              className="absolute top-0 left-0 h-full bg-blue-500 transition-all"
              style={{ width: `${uploadProgress}%` }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center text-xs">
              {uploadProgress}%
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
      
      {/* Debug info displayed on the UI */}
      <p className="text-xs bg-gray-100 p-1 mt-1">{debugInfo}</p>
      
      <p className="text-xs text-gray-500 mt-1">
        Upload an MP4, WebM, or OGG video file (max 100MB) or provide a URL above
      </p>
    </div>
  );
};

export default VideoUploader;